import type { CoveragePolicy, Scenario, ScenarioCatalogItem } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, Plus, ShieldOff, ShieldQuestion } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useMemo, useState } from "react";
import { GraceRing, useNow } from "@/components/domain/countdown";
import { StatTile } from "@/components/domain/stat";
import { CoverageChip } from "@/components/domain/status";
import { ErrorPanel, QueryState } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { EmptyState, Meter, Skeleton } from "@/components/ui/misc";
import { MAGNITUDE_COLOR } from "@/features/agents/bits";
import { ScenarioMatrix } from "@/features/agents/scenario-matrix";
import { isAvailable } from "@/features/coverage/quote-model";
import { ImpactKindTag, LossTypeChips, PolicyChip, PolicyStatusBadge, TermsBadge, lossTypesOf, policyColors } from "@/features/coverage/policy-bits";
import { ScenarioSheet, ScenarioTable, sortScenarios } from "@/features/coverage/scenario-table";
import { q } from "@/lib/api";
import { fmtMoney, fmtPct, fmtRemaining, fmtShortDate } from "@/lib/format";

export const Route = createFileRoute("/_app/agents/$agentId/coverage")({
  component: CoverageTab,
});

function CoverageTab() {
  const { agentId } = Route.useParams();
  const agent = useQuery(q.agent(agentId));
  const scenarios = useQuery(q.scenarios({ agentId }));
  const policies = useQuery(q.policies());
  const endorsements = useQuery(q.endorsements());
  const catalog = useQuery(q.scenarioCatalog([agentId]));
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const sorted = useMemo(() => (scenarios.data ? sortScenarios(scenarios.data) : undefined), [scenarios.data]);
  const colors = useMemo(() => policyColors(policies.data), [policies.data]);
  const groups = useMemo(() => {
    const byPolicy = new Map<string, Scenario[]>();
    for (const s of sorted ?? []) byPolicy.set(s.policyId, [...(byPolicy.get(s.policyId) ?? []), s]);
    return [...byPolicy.entries()].map(([id, list]) => ({ id, name: list[0]!.policyName, policy: policies.data?.find((p) => p.id === id), scenarios: list }));
  }, [sorted, policies.data]);
  const uncovered = (catalog.data ?? []).filter(isAvailable);
  const ineligible = (catalog.data ?? []).filter((c) => !c.eligible && !(sorted ?? []).some((s) => s.key === c.key));

  return (
    <QueryState
      query={scenarios}
      loading={
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-4 gap-3">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-48" />
          <Skeleton className="h-64" />
        </div>
      }
    >
      {() => (
        <div className="flex flex-col gap-4">
          <CoverageStats rows={sorted ?? []} insuredLimit={agent.data?.insuredLimit.amount} policyCount={agent.data?.policyIds.length ?? groups.length} />

          <Card>
            <CardHeader
              title="Insure more scenarios"
              description={
                catalog.isPending
                  ? "Checking this agent's scenario catalog…"
                  : uncovered.length
                    ? `${uncovered.length} eligible ${uncovered.length === 1 ? "scenario isn't" : "scenarios aren't"} covered by any policy yet. Losses in them are not insured.`
                    : "Every eligible scenario in this agent's catalog is covered by a policy."
              }
              actions={
                <Button size="sm" variant={uncovered.length ? "default" : "outline"} asChild>
                  <Link to="/coverage/quote" search={{ agents: agentId }}>
                    <Plus /> Insure more scenarios
                  </Link>
                </Button>
              }
            />
            {catalog.error ? (
              <CardBody>
                <ErrorPanel error={catalog.error} reset={() => void catalog.refetch()} />
              </CardBody>
            ) : catalog.isPending ? (
              <CardBody>
                <Skeleton className="h-20" />
              </CardBody>
            ) : uncovered.length || ineligible.length ? (
              <CatalogList items={[...uncovered, ...ineligible]} agentId={agentId} />
            ) : null}
          </Card>

          {!sorted?.length ? (
            <Card>
              <EmptyState
                icon={<ShieldQuestion />}
                title="No policy covers this agent yet"
                description="Quote a policy over this agent's scenarios. Scenarios become insurable once its action classes are observed and confirmed on the Risk profile tab."
                action={
                  <Button size="sm" asChild className="mt-2">
                    <Link to="/coverage/quote" search={{ agents: agentId }}>
                      Get a quote <ArrowRight />
                    </Link>
                  </Button>
                }
              />
            </Card>
          ) : (
            <>
              {groups.map((g) => (
                <Card key={g.id}>
                  <CardHeader
                    title={
                      <span className="flex flex-wrap items-center gap-2">
                        <PolicyChip id={g.id} name={g.name} color={colors.get(g.id)} />
                        {g.policy ? <PolicyStatusBadge status={g.policy.status} /> : null}
                        {g.policy ? <TermsBadge mode={g.policy.mode} /> : null}
                      </span>
                    }
                    description={g.policy ? policyLine(g.policy, g.scenarios) : undefined}
                    actions={
                      <Link to="/coverage/policies/$policyId" params={{ policyId: g.id }} className="inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
                        Policy <ArrowRight className="size-3" />
                      </Link>
                    }
                  />
                  <ScenarioTable scenarios={g.scenarios} endorsements={endorsements.data} showAgent={false} showPolicy={false} onSelect={(s) => setSelectedId(s.id)} />
                </Card>
              ))}
              <Card>
                <CardHeader title="Scenario matrix" description="Action classes by coverage state across all policies. Hover a state for what it means." />
                <ScenarioMatrix scenarios={sorted} />
              </Card>
            </>
          )}
          <ScenarioSheet scenario={sorted?.find((s) => s.id === selectedId) ?? null} endorsements={endorsements.data} onClose={() => setSelectedId(null)} />
        </div>
      )}
    </QueryState>
  );
}

function policyLine(p: CoveragePolicy, scenarios: Scenario[]) {
  const sub = scenarios.reduce((n, s) => n + s.subLimit.amount, 0);
  return `${p.carrier} · ${p.policyNumber} · ${scenarios.length} ${scenarios.length === 1 ? "scenario" : "scenarios"} on this agent · ${fmtMoney(sub, { compact: true })} in sub-limits · per claim ${fmtMoney(p.perClaimLimit, { compact: true })}`;
}

function CatalogList({ items, agentId }: { items: ScenarioCatalogItem[]; agentId: string }) {
  return (
    <ul className="divide-y">
      {items.map((c) => (
        <li key={c.key} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-fg">{c.label}</div>
            <div className="truncate text-xs text-fg-3">
              <span className="font-mono text-[11.5px]">{c.key}</span> · {c.envelope}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <LossTypeChips types={c.lossTypes} />
              <ImpactKindTag kind={c.impactKind} />
            </div>
          </div>
          {c.eligible ? (
            <>
              <Badge tone="warning">
                <ShieldOff /> Not insured
              </Badge>
              <span className="w-56 text-right text-xs text-fg-3 tabular">
                {fmtMoney(c.ratePer1k, { cents: true })} per $1k · suggested {fmtMoney(c.suggestedSubLimit, { compact: true })}
              </span>
              <Button variant="outline" size="sm" asChild>
                <Link to="/coverage/quote" search={{ agents: agentId }}>
                  Quote
                </Link>
              </Button>
            </>
          ) : (
            <>
            <CoverageChip state={c.state} />
            <span className="inline-flex max-w-[22rem] items-start gap-1.5 text-xs text-fg-2">
              <AlertTriangle className="mt-px size-3.5 shrink-0 text-fg-3" aria-hidden />
              Not eligible: {c.ineligibleReason ?? "not insurable in its current state"}
            </span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function CoverageStats({ rows, insuredLimit, policyCount }: { rows: Scenario[]; insuredLimit?: number; policyCount: number }) {
  const now = useNow();
  const covered = rows.filter((r) => r.state === "covered" || r.state === "mitigated").length;
  const atRisk = rows.filter((r) => r.state === "grace" || r.state === "verifying" || r.state === "graduated").length;
  const notCovered = rows.filter((r) => r.state === "excluded" || r.state === "unobserved").length;
  const limit = rows.reduce((n, r) => n + r.subLimit.amount, 0);
  const used = rows.reduce((n, r) => n + r.used.amount, 0);
  const next = rows.filter((r) => r.graceDeadline && Date.parse(r.graceDeadline) > now).sort((a, b) => Date.parse(a.graceDeadline!) - Date.parse(b.graceDeadline!))[0];
  return (
    <section aria-label="Coverage summary" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile
        label="Insured limit"
        value={insuredLimit !== undefined ? fmtMoney(insuredLimit, { compact: true }) : "—"}
        sub={`across ${policyCount} ${policyCount === 1 ? "policy" : "policies"} covering this agent`}
      />
      <StatTile
        label="Scenarios"
        value={rows.length}
        sub={`${covered} covered · ${atRisk} at risk · ${notCovered} not covered`}
        footer={rows.length ? <LossTypeChips types={lossTypesOf(rows)} /> : null}
      />
      <StatTile
        label="Sub-limits used"
        value={fmtMoney(used, { compact: true })}
        sub={`${fmtPct(limit ? used / limit : 0, 1)} of ${fmtMoney(limit, { compact: true })} in scenario sub-limits`}
        footer={<Meter value={used} max={limit || 1} tone={MAGNITUDE_COLOR} label="Sub-limits used" />}
      />
      <StatTile
        label="Next scenario deadline"
        value={
          next ? (
            <span className="inline-flex items-center gap-2">
              {next.graceStartedAt ? <GraceRing start={next.graceStartedAt} deadline={next.graceDeadline!} size={22} stroke={3} /> : null}
              {fmtRemaining(Date.parse(next.graceDeadline!) - now)}
            </span>
          ) : (
            <span className="text-fg-3">None</span>
          )
        }
        sub={
          next ? (
            <span className="block truncate" title={next.key}>
              <span className="font-mono text-[11px] text-fg-2">{next.key}</span> · closes {fmtShortDate(next.graceDeadline!)}
            </span>
          ) : (
            "No scenario is in a grace window"
          )
        }
      />
    </section>
  );
}
